import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderUp,
  Loader2,
  Pause,
  Play,
  Plus,
  Users,
  X,
} from 'lucide-react';
import './CharactersPage.css';
import './CharacterCreateModal.css';
import { useAuthContext } from './components/AuthProvider';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { createCharacter, generateSeedVoicePreview, saveSeedVoice } from './lib/api';
import {
  useCharactersData,
  fetchCharactersIfStale,
  useLibraryCharactersData,
  fetchLibraryCharactersIfStale,
  useCharactersStore,
  fetchCharacterDetailIfStale,
} from './stores';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_VOICE_REFERENCE_BYTES = 10 * 1024 * 1024;
const DEFAULT_PREVIEW_SCRIPT = 'Every great story starts with a voice you remember.';
const SUPPORTED_VOICE_REFERENCE_EXTENSIONS = [
  '.wav', '.mp3', '.ogg', '.m4a',
  '.mp4', '.mov', '.webm', '.m4v', '.mkv', '.avi',
];

const FILTER_GROUPS = {
  Sex: ['male', 'female'],
  Age: ['young adult', 'middle aged', 'old'],
  Race: ['white', 'black', 'asian', 'hispanic', 'middle eastern', 'south asian'],
};

function hasKeyword(keywords, value) {
  if (!keywords || !Array.isArray(keywords)) return false;
  return keywords.some((k) => k.toLowerCase() === value.toLowerCase());
}

function CharacterCard({ character, onClick, onHover }) {
  const description = character.personality || '';
  const truncated = description.length > 80 ? description.slice(0, 80) + '...' : description;
  const keywordsPreview = character.keywords?.slice(0, 4)?.join(', ');
  const imageUrl = character.assetImageUrl || character.imageUrl || character.asset?.imageUrl;

  return (
    <div className="character-card" onClick={onClick} role="button" tabIndex={0}
      onMouseEnter={onHover}
      onFocus={onHover}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}>
      <div className="character-card-image">
        {imageUrl ? (
          <img src={imageUrl} alt={character.name || 'Character'} className="character-card-img" />
        ) : (
          <div className="character-card-placeholder">
            {character.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
      </div>
      <div className="character-card-info">
        {character.name && <span className="character-card-name">{character.name}</span>}
        {truncated ? (
          <span className="character-card-description">{truncated}</span>
        ) : keywordsPreview ? (
          <span className="character-card-description">{keywordsPreview}</span>
        ) : null}
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export function buildCharacterVoicePrompt({ voiceDescription, previewScript, voiceReference }) {
  const parts = [];
  if (voiceReference) {
    parts.push("The speaker's voice matches @Audio1 exactly, including its timbre, accent, cadence, and delivery.");
  } else if (voiceDescription?.trim()) {
    parts.push(voiceDescription.trim());
  }
  parts.push(`Say the following exactly: "${previewScript.trim()}"`);
  return parts.join(' ');
}

function VoicePreviewPlayer({ src, durationHint }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(Number(durationHint) || 0);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(Number(durationHint) || 0);
  }, [src, durationHint]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    const nextTime = Number(event.target.value);
    if (!audio || !Number.isFinite(nextTime)) return;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  return (
    <div className="character-voice-player">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
      />
      <button
        type="button"
        className="character-voice-player-toggle"
        onClick={togglePlayback}
        aria-label={isPlaying ? 'Pause voice preview' : 'Play voice preview'}
      >
        {isPlaying ? <Pause size={15} /> : <Play size={15} />}
      </button>
      <input
        className="character-voice-player-progress"
        type="range"
        min="0"
        max={duration || 0}
        step="0.01"
        value={Math.min(currentTime, duration || 0)}
        onChange={handleSeek}
        aria-label="Voice preview progress"
      />
      <span className="character-voice-player-time">
        {formatDuration(currentTime)} / {formatDuration(duration)}
      </span>
    </div>
  );
}

export function CreateCharacterModal({ isOpen, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [voiceDescription, setVoiceDescription] = useState('');
  const [physicalAppearance, setPhysicalAppearance] = useState('');
  const [voiceMode, setVoiceMode] = useState('description');
  const [voiceReference, setVoiceReference] = useState(null);
  const [previewScript, setPreviewScript] = useState(DEFAULT_PREVIEW_SCRIPT);
  const [voicePreview, setVoicePreview] = useState(null);
  const [savedVoiceId, setSavedVoiceId] = useState(null);
  const [isDraggingVoice, setIsDraggingVoice] = useState(false);
  const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const voiceFileInputRef = useRef(null);

  const isBusy = isSaving || isGeneratingVoice;
  const hasVoiceSource = voiceMode === 'reference'
    ? !!voiceReference
    : voiceDescription.trim().length > 0;
  const canGenerateVoice = hasVoiceSource && previewScript.trim().length > 0 && !isBusy;
  const voiceSetupIncomplete = voiceMode === 'reference' && !voiceReference;
  const voiceNeedsPreview = hasVoiceSource && !voicePreview;
  const canCreate = name.trim().length > 0 && !voiceSetupIncomplete && !voiceNeedsPreview && !isBusy;

  const invalidateVoicePreview = useCallback(() => {
    setVoicePreview(null);
    setSavedVoiceId(null);
    setError(null);
  }, []);

  const resetForm = useCallback(() => {
    setName('');
    setVoiceDescription('');
    setPhysicalAppearance('');
    setVoiceMode('description');
    setVoiceReference(null);
    setPreviewScript(DEFAULT_PREVIEW_SCRIPT);
    setVoicePreview(null);
    setSavedVoiceId(null);
    setIsDraggingVoice(false);
    setIsGeneratingVoice(false);
    setIsSaving(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (isBusy) return;
    resetForm();
    onClose();
  }, [isBusy, onClose, resetForm]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const setReferenceFile = (file) => {
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (!SUPPORTED_VOICE_REFERENCE_EXTENSIONS.some((extension) => lowerName.endsWith(extension))) {
      setError('Upload a supported audio or video voice reference.');
      return;
    }
    if (file.size > MAX_VOICE_REFERENCE_BYTES) {
      setError('Voice references must be 10 MB or smaller.');
      return;
    }
    setVoiceReference(file);
    invalidateVoicePreview();
  };

  const handleVoiceModeChange = (mode) => {
    setVoiceMode(mode);
    setVoiceReference(null);
    invalidateVoicePreview();
  };

  const handleGenerateVoice = async () => {
    if (!canGenerateVoice) return;
    try {
      setIsGeneratingVoice(true);
      setError(null);
      const prompt = buildCharacterVoicePrompt({
        voiceDescription,
        previewScript,
        voiceReference: voiceMode === 'reference' ? voiceReference : null,
      });
      const result = await generateSeedVoicePreview({
        prompt,
        audioFiles: voiceMode === 'reference' && voiceReference ? [voiceReference] : [],
      });
      if (!result?.previewUrl) throw new Error('No preview audio was returned');
      setVoicePreview({ previewUrl: result.previewUrl, duration: result.duration });
      setSavedVoiceId(null);
    } catch (err) {
      setError('Failed to generate voice preview');
    } finally {
      setIsGeneratingVoice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    try {
      setIsSaving(true);
      setError(null);
      let voiceId = savedVoiceId;
      if (voicePreview && !voiceId) {
        const voiceResult = await saveSeedVoice({
          name: `${name.trim()} Voice`,
          description: voiceMode === 'reference'
            ? `Voice created from reference media: ${voiceReference.name}`
            : voiceDescription.trim(),
          previewUrl: voicePreview.previewUrl,
        });
        voiceId = voiceResult?.voice?.id;
        if (!voiceId) throw new Error('Voice preview could not be saved');
        setSavedVoiceId(voiceId);
      }
      const resp = await createCharacter({
        name: name.trim(),
        personality: voiceMode === 'description' ? (voiceDescription.trim() || null) : null,
        physicalAppearance: physicalAppearance.trim() || null,
        voiceId: voiceId || null,
      });
      if (resp.character) {
        onCreated(resp.character);
        resetForm();
        onClose();
      }
    } catch (err) {
      setError('Failed to create character');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="character-modal-overlay" onClick={handleClose}>
      <div
        className="character-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="character-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="character-modal-header">
          <div className="character-modal-heading-copy">
            <h2 id="character-create-title" className="character-modal-title">Create a character</h2>
            <p>Define the character and audition their voice before saving.</p>
          </div>
          <button
            type="button"
            className="character-modal-close"
            onClick={handleClose}
            disabled={isBusy}
            aria-label="Close character creator"
          >
            <X size={17} />
          </button>
        </header>

        <form
          className="character-modal-form"
          onSubmit={handleSubmit}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canCreate) {
              event.preventDefault();
              event.currentTarget.requestSubmit();
            }
          }}
        >
          <div className="character-modal-body">
            <section className="character-form-panel" aria-labelledby="character-identity-heading">
              <div className="character-form-panel-heading">
                <span className="character-form-step">01</span>
                <div>
                  <h3 id="character-identity-heading">Character identity</h3>
                  <p>The reusable visual profile.</p>
                </div>
              </div>

              <label className="character-modal-label">
                <span>Name <em>Required</em></span>
                <input
                  className="character-modal-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Professor Oak"
                  maxLength={500}
                  autoFocus
                />
              </label>

              <label className="character-modal-label character-modal-label--grow">
                <span>Physical appearance <em>Optional</em></span>
                <textarea
                  className="character-modal-textarea character-modal-textarea--appearance"
                  value={physicalAppearance}
                  onChange={(e) => setPhysicalAppearance(e.target.value)}
                  placeholder="Hair, face, clothing, build, age, and other visual details…"
                  maxLength={5000}
                  rows={7}
                />
                <small>A portrait will be generated from this description.</small>
              </label>
            </section>

            <section className="character-form-panel character-form-panel--voice" aria-labelledby="character-voice-heading">
              <div className="character-form-panel-heading">
                <span className="character-form-step">02</span>
                <div>
                  <h3 id="character-voice-heading">Voice & preview</h3>
                  <p>Design a voice or match a recording.</p>
                </div>
              </div>

              <div className="character-voice-source">
                <span className="character-field-label">Voice source</span>
                <div className="character-voice-source-options">
                  <button
                    type="button"
                    className={voiceMode === 'description' ? 'active' : ''}
                    onClick={() => handleVoiceModeChange('description')}
                    aria-pressed={voiceMode === 'description'}
                  >
                    <span><strong>Describe a voice</strong><small>Generate from direction</small></span>
                  </button>
                  <button
                    type="button"
                    className={voiceMode === 'reference' ? 'active' : ''}
                    onClick={() => handleVoiceModeChange('reference')}
                    aria-pressed={voiceMode === 'reference'}
                  >
                    <span><strong>Upload reference</strong><small>Match an existing voice</small></span>
                  </button>
                </div>
              </div>

              {voiceMode === 'description' ? (
                <label className="character-modal-label">
                  <span>Voice description <em>Optional</em></span>
                  <textarea
                    className="character-modal-textarea"
                    value={voiceDescription}
                    onChange={(e) => {
                      setVoiceDescription(e.target.value);
                      invalidateVoicePreview();
                    }}
                    placeholder="Warm, grounded alto with a measured cadence, subtle rasp, and reassuring energy…"
                    maxLength={2000}
                    rows={4}
                  />
                  <small>Describe tone, age, accent, cadence, texture, and delivery.</small>
                </label>
              ) : (
                <div className="character-modal-label">
                  <span>Voice reference <em>Required</em></span>
                  <button
                    type="button"
                    className={`character-voice-upload ${isDraggingVoice ? 'dragging' : ''} ${voiceReference ? 'has-file' : ''}`}
                    onClick={() => voiceFileInputRef.current?.click()}
                    onDragEnter={(event) => { event.preventDefault(); setIsDraggingVoice(true); }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragLeave={() => setIsDraggingVoice(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setIsDraggingVoice(false);
                      setReferenceFile(event.dataTransfer.files?.[0]);
                    }}
                  >
                    <FolderUp size={16} className="character-voice-upload-icon" aria-hidden="true" />
                    {voiceReference ? (
                      <>
                        <span className="character-voice-upload-copy">
                          <strong>{voiceReference.name}</strong>
                          <small>{(voiceReference.size / (1024 * 1024)).toFixed(1)} MB · Click to replace</small>
                        </span>
                        <span className="character-voice-upload-status">Ready</span>
                      </>
                    ) : (
                      <span className="character-voice-upload-copy">
                        <strong>Drop an audio or video file here or click to browse</strong>
                        <small>Audio or video · 10 MB max · only audio is used</small>
                      </span>
                    )}
                  </button>
                  <input
                    ref={voiceFileInputRef}
                    className="character-voice-file-input"
                    type="file"
                    accept=".wav,.mp3,.ogg,.m4a,.mp4,.mov,.webm,.m4v,.mkv,.avi,audio/*,video/*"
                    onChange={(event) => {
                      setReferenceFile(event.target.files?.[0]);
                      event.target.value = '';
                    }}
                    aria-label="Upload voice reference"
                  />
                </div>
              )}

              <label className="character-modal-label">
                <span>Preview script <em>Required for preview</em></span>
                <textarea
                  className="character-modal-textarea character-modal-textarea--script"
                  value={previewScript}
                  onChange={(e) => {
                    setPreviewScript(e.target.value);
                    invalidateVoicePreview();
                  }}
                  placeholder="Write the exact line you want to hear…"
                  maxLength={1000}
                  rows={3}
                />
                <small>{previewScript.length}/1,000 characters</small>
              </label>

              <div className={`character-voice-preview ${voicePreview ? 'ready' : ''}`}>
                {voicePreview ? (
                  <>
                    <div className="character-voice-preview-heading">
                      <span>Voice preview</span>
                      <button type="button" onClick={handleGenerateVoice} disabled={!canGenerateVoice}>
                        {isGeneratingVoice && <Loader2 size={12} className="character-spin" />}
                        {isGeneratingVoice ? 'Regenerating…' : 'Regenerate'}
                      </button>
                    </div>
                    <VoicePreviewPlayer src={voicePreview.previewUrl} durationHint={voicePreview.duration} />
                  </>
                ) : (
                  <>
                    <div className="character-voice-preview-empty">
                      <div>
                        <strong>Voice preview</strong>
                        <small>{hasVoiceSource ? 'Your source and script are ready.' : 'Add a voice description or reference first.'}</small>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="character-voice-generate"
                      onClick={handleGenerateVoice}
                      disabled={!canGenerateVoice}
                    >
                      {isGeneratingVoice && <Loader2 size={13} className="character-spin" />}
                      {isGeneratingVoice ? 'Generating preview…' : 'Generate preview'}
                    </button>
                  </>
                )}
              </div>
            </section>
          </div>

          <footer className="character-modal-footer">
            <div className="character-modal-feedback" aria-live="polite">
              {error ? <span className="character-modal-error">{error}</span> : (
                <span>{voiceNeedsPreview ? 'Generate a voice preview to continue.' : 'Character details stay editable after creation.'}</span>
              )}
            </div>
            <div className="character-modal-actions">
              <button type="button" className="character-modal-cancel" onClick={handleClose} disabled={isBusy}>Cancel</button>
              <button
                type="submit"
                className="character-modal-submit"
                disabled={!canCreate}
                aria-keyshortcuts="Meta+Enter Control+Enter"
                aria-label={voicePreview ? 'Create character with voice' : 'Create character'}
              >
                {isSaving && <Loader2 size={13} className="character-spin" />}
                {isSaving ? 'Creating character…' : voicePreview ? 'Create character with voice' : 'Create character'}
                {!isSaving && <kbd>⌘↵</kbd>}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}

function FilterBar({ activeFilters, onToggle, onClear }) {
  if (Object.values(activeFilters).every((v) => v.length === 0)) {
    return null;
  }

  const allActive = Object.entries(activeFilters).flatMap(([group, values]) =>
    values.map((v) => ({ group, value: v }))
  );

  return (
    <div className="characters-filter-active">
      {allActive.map(({ group, value }) => (
        <button
          key={`${group}-${value}`}
          className="characters-filter-chip active"
          onClick={() => onToggle(group, value)}
        >
          {value}
          <X size={11} />
        </button>
      ))}
      <button className="characters-filter-clear" onClick={onClear}>
        Clear all
      </button>
    </div>
  );
}

function CharactersPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthContext();

  const addCharacter = useCharactersStore((s) => s.addCharacter);

  const { items: characters, isLoading: myLoading, error: myError, hasCache: myHasCache } = useCharactersData(user?.id);
  const { items: libraryCharacters, isLoading: libLoading, error: libError, hasCache: libHasCache } = useLibraryCharactersData(user?.id);

  const [activeTab, setActiveTab] = useState('my');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ Sex: [], Age: [], Race: [], Other: [] });
  const [otherKeywords, setOtherKeywords] = useState([]);

  const isLoading = activeTab === 'my' ? (myLoading && !myHasCache) : (libLoading && !libHasCache);
  const hasCache = activeTab === 'my' ? myHasCache : libHasCache;
  const error = activeTab === 'my' ? myError : libError;

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  useEffect(() => {
    if (loading || !user) return;
    fetchCharactersIfStale({ userId: user.id });
    fetchLibraryCharactersIfStale({ userId: user.id });
  }, [user, loading]);

  // Build "Other" keywords from library characters (excluding known filter values)
  useEffect(() => {
    const knownValues = new Set(
      Object.values(FILTER_GROUPS).flat().map((v) => v.toLowerCase())
    );
    const otherSet = new Set();
    libraryCharacters.forEach((c) => {
      (c.keywords || []).forEach((kw) => {
        const lower = kw.toLowerCase();
        if (!knownValues.has(lower)) otherSet.add(lower);
      });
    });
    setOtherKeywords(Array.from(otherSet).sort());
  }, [libraryCharacters]);

  const toggleFilter = useCallback((group, value) => {
    setActiveFilters((prev) => {
      const current = prev[group] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [group]: next };
    });
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters({ Sex: [], Age: [], Race: [], Other: [] });
  }, []);

  const hasAnyFilter = Object.values(activeFilters).some((v) => v.length > 0);

  const sourceList = activeTab === 'my' ? characters : libraryCharacters;

  const filteredCharacters = useMemo(() => {
    let list = sourceList;

    // Text search — name, personality, and keywords
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter((c) => {
        if (c.name?.toLowerCase().includes(q)) return true;
        if (c.personality?.toLowerCase().includes(q)) return true;
        if (c.keywords?.some((kw) => kw.toLowerCase().includes(q))) return true;
        return false;
      });
    }

    // Keyword filters — all selected values within a group are OR'd; groups are AND'd
    const filterEntries = Object.entries(activeFilters).filter(([, values]) => values.length > 0);
    if (filterEntries.length > 0) {
      list = list.filter((c) =>
        filterEntries.every(([, values]) =>
          values.some((v) => hasKeyword(c.keywords, v))
        )
      );
    }

    return list;
  }, [sourceList, debouncedSearch, activeFilters]);

  return (
    <AuthenticatedLayout>
      <div className="characters-page-content">
        <div className="characters-page-header-section">
          <div className="characters-page-header-left">
            <h1 className="characters-page-title">Characters</h1>
            <p className="characters-page-subtitle">
              Reusable characters shared across all your projects
            </p>
          </div>
          {activeTab === 'my' && (
            <button className="characters-page-new-button" onClick={() => setShowCreate(true)}>
              <Plus size={14} />
              New Character
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="characters-tabs">
          <button
            className={`characters-tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            My Characters
          </button>
          <button
            className={`characters-tab ${activeTab === 'library' ? 'active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            Character Library
          </button>
        </div>

        {activeTab === 'library' ? (
          <div className="characters-library-layout">
            {/* Sidebar */}
            <aside className="characters-sidebar">
              <input
                type="text"
                className="characters-sidebar-search"
                placeholder="Search keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              {Object.entries(FILTER_GROUPS).map(([group, values]) => (
                <div key={group} className="characters-filter-group">
                  <span className="characters-filter-group-label">{group}</span>
                  <div className="characters-filter-chips">
                    {values.map((value) => (
                      <button
                        key={value}
                        className={`characters-filter-chip ${activeFilters[group]?.includes(value) ? 'active' : ''}`}
                        onClick={() => toggleFilter(group, value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {otherKeywords.length > 0 && (
                <div className="characters-filter-group">
                  <span className="characters-filter-group-label">Other</span>
                  <div className="characters-filter-chips">
                    {otherKeywords.slice(0, 30).map((value) => (
                      <button
                        key={value}
                        className={`characters-filter-chip ${activeFilters.Other?.includes(value) ? 'active' : ''}`}
                        onClick={() => toggleFilter('Other', value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasAnyFilter && (
                <button className="characters-filter-clear" onClick={clearFilters}>
                  Clear all filters
                </button>
              )}
            </aside>

            {/* Main content */}
            <div className="characters-library-main">
              {hasAnyFilter && (
                <FilterBar activeFilters={activeFilters} onToggle={toggleFilter} onClear={clearFilters} />
              )}

              {error && <div className="characters-page-error">{error}</div>}
              {isLoading && <div className="characters-page-loading">Loading characters...</div>}

              {hasCache && !error && filteredCharacters.length === 0 && (
                <div className="characters-page-empty">
                  <Users size={40} className="characters-page-empty-icon" />
                  {debouncedSearch || hasAnyFilter ? (
                    <p>No characters match your filters.</p>
                  ) : (
                    <p>No characters in the library yet.</p>
                  )}
                </div>
              )}

              {hasCache && filteredCharacters.length > 0 && (
                <div className="characters-page-grid">
                  {filteredCharacters.map((character) => (
                    <CharacterCard
                      key={character.id}
                      character={character}
                      onClick={() => navigate(`/characters/${character.id}`)}
                      onHover={() => fetchCharacterDetailIfStale({ id: character.id, userId: user?.id })}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="characters-my-layout">
            <div className="characters-my-search-sticky">
              <input
                type="text"
                className="characters-page-search"
                placeholder="Search characters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="characters-my-scroll">
              {error && <div className="characters-page-error">{error}</div>}
              {isLoading && <div className="characters-page-loading">Loading characters...</div>}

              {hasCache && !error && filteredCharacters.length === 0 && (
                <div className="characters-page-empty">
                  <Users size={40} className="characters-page-empty-icon" />
                  {debouncedSearch ? (
                    <p>No characters match your search.</p>
                  ) : (
                    <>
                      <p>No characters yet.</p>
                      <p className="characters-page-empty-hint">
                        Create your first character to reuse across all projects.
                      </p>
                      <button className="characters-page-empty-cta" onClick={() => setShowCreate(true)}>
                        <Plus size={14} />
                        Create your first character
                      </button>
                    </>
                  )}
                </div>
              )}

              {hasCache && filteredCharacters.length > 0 && (
                <div className="characters-page-grid">
                  {filteredCharacters.map((character) => (
                    <CharacterCard
                      key={character.id}
                      character={character}
                      onClick={() => navigate(`/characters/${character.id}`)}
                      onHover={() => fetchCharacterDetailIfStale({ id: character.id, userId: user?.id })}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <CreateCharacterModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(character) => addCharacter(character)}
      />
    </AuthenticatedLayout>
  );
}

export default CharactersPage;
